import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { store } from '../index';
import { setDarkMode } from './uiSlice';

import { API_URL } from '../../config/api';
import { invalidateFileUrlCache } from '../../services/fileUrlService';
import websocketService from '../../services/websocketService';
import activityService from '../../services/activityService';
import otpService from '../../services/otpService';
import { stopRealmSync } from '../../realm';
import {
  getRememberedAccounts,
  isRememberMePreferred,
  saveRememberedAccount,
  ensureAvatarCached,
} from '../../services/rememberedAccounts';
import { syncWorkModeFromStatus } from '../../utils/workModePrefs';

export const NETWORK_ERROR_MESSAGE = 'Network issue. Try again please.';
const USER_STORAGE_KEY = 'workloop_user';

const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const persistCachedUser = (user) => {
  try {
    if (!user) {
      localStorage.removeItem(USER_STORAGE_KEY);
      return;
    }
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } catch {
    // Ignore quota / private-mode failures
  }
};

const isTransientAuthError = (error) => {
  const status = error?.response?.status;
  const code = error?.code;
  return code === 'ERR_CANCELED'
    || code === 'TAB_SESSION_BLOCKED'
    || code === 'ERR_NETWORK'
    || code === 'ECONNABORTED'
    || error?.message === 'TAB_SESSION_BLOCKED'
    || !error?.response
    || status === 502
    || status === 503
    || status === 504
    || status === 429;
};

const looksLikeHtml = (value) => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim().toLowerCase();
  return trimmed.startsWith('<!doctype')
    || trimmed.startsWith('<html')
    || trimmed.includes('<html')
    || trimmed.includes('502 - web server')
    || trimmed.includes('503 -')
    || trimmed.includes('504 -');
};

const extractErrorMessage = (error, fallbackMessage) => {
  const status = error?.response?.status;
  const payload = error?.response?.data;

  // IIS/proxy HTML (502 during deploy) or no response (backend down)
  if (!error?.response
      || status === 502
      || status === 503
      || status === 504
      || error?.code === 'ERR_NETWORK'
      || error?.message === 'Network Error') {
    return NETWORK_ERROR_MESSAGE;
  }

  if (typeof payload === 'string') {
    if (looksLikeHtml(payload)) {
      return NETWORK_ERROR_MESSAGE;
    }
    if (payload.trim()) {
      return payload;
    }
  }

  if (payload && typeof payload === 'object') {
    if (typeof payload.message === 'string' && payload.message.trim()) {
      return looksLikeHtml(payload.message) ? NETWORK_ERROR_MESSAGE : payload.message;
    }
    if (typeof payload.error === 'string' && payload.error.trim()) {
      return looksLikeHtml(payload.error) ? NETWORK_ERROR_MESSAGE : payload.error;
    }
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    if (looksLikeHtml(error.message) || /network/i.test(error.message)) {
      return NETWORK_ERROR_MESSAGE;
    }
    return error.message;
  }

  return fallbackMessage;
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        username,
        password,
      });
      
      // Store tokens in localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
      try {
        if (isRememberMePreferred() || getRememberedAccounts().some((item) => item.username === response.data.username)) {
          await Promise.race([
            saveRememberedAccount(response.data),
            new Promise((resolve) => setTimeout(resolve, 4000)),
          ]);
        }
      } catch {
        // Photo snapshot must not block login
      }
      return response.data;
    } catch (error) {
      const message = extractErrorMessage(error, 'Login failed');
      const lowered = String(message).toLowerCase();
      if (error?.response?.status === 401 || lowered.includes('bad credentials')) {
        return rejectWithValue('Invalid credentials');
      }
      return rejectWithValue(message);
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async ({ username, email, password, firstName, lastName, organizationSlug, mobile }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        username,
        email,
        password,
        firstName,
        lastName,
        organizationSlug,
        mobile,
      });
      
      // Store tokens in localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
      
      return response.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Registration failed'));
    }
  }
);

export const organizationSignup = createAsyncThunk(
  'auth/organizationSignup',
  async ({ organizationName, organizationSlug, ownerEmail, ownerUsername, ownerPassword, ownerMobile }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/organizations/signup`, {
        organizationName,
        organizationSlug,
        ownerEmail,
        ownerUsername,
        ownerPassword,
        ownerMobile,
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Organization signup failed'));
    }
  }
);

export const updatePreferredLanguage = createAsyncThunk(
  'auth/updatePreferredLanguage',
  async (preferredLanguage, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return preferredLanguage;
      const response = await axios.put(
        `${API_URL}/profile`,
        { preferredLanguage },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data?.preferredLanguage || preferredLanguage;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to save language');
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async (_, { getState }) => {
  const currentUser = getState()?.auth?.user || readCachedUser();
  const alreadyRemembered = Boolean(
    currentUser?.username
    && getRememberedAccounts().some((item) => item.username === currentUser.username)
  );
  if (currentUser && (isRememberMePreferred() || alreadyRemembered)) {
    try {
      await saveRememberedAccount(currentUser);
    } catch {
      // Keep logout going even if avatar snapshot fails
    }
  }

  // Track logout activity before clearing credentials
  activityService.trackLogout();

  websocketService.disconnect();
  await stopRealmSync({ wipe: false });
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem(USER_STORAGE_KEY);
  localStorage.removeItem('chat_lastViewed');
  invalidateFileUrlCache();
});

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return rejectWithValue({
          message: 'No token found',
          status: 401,
          transient: false,
        });
      }
      
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data?.username) {
        const remembered = getRememberedAccounts().some((item) => item.username === response.data.username);
        if (isRememberMePreferred() || remembered) {
          saveRememberedAccount(response.data).catch(() => {});
        } else if (response.data.profilePicture) {
          ensureAvatarCached(response.data.profilePicture, response.data.username).catch(() => {});
        }
      }

      return response.data;
    } catch (error) {
      const status = error?.response?.status || null;
      return rejectWithValue({
        message: extractErrorMessage(error, 'Failed to get current user'),
        status,
        transient: status !== 401 && isTransientAuthError(error),
      });
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  'auth/updateUserStatus',
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return rejectWithValue('No token found');
      }
      const body = typeof payload === 'string'
        ? { status: payload }
        : {
            ...(payload?.status ? { status: payload.status } : {}),
            ...(payload?.workLocation ? { workLocation: payload.workLocation } : {}),
            ...(payload?.statusMessage != null ? { statusMessage: payload.statusMessage } : {}),
            ...(payload?.source ? { source: payload.source } : {}),
          };
      if (!body.status && !body.workLocation) {
        return rejectWithValue('status or workLocation is required');
      }
      const response = await axios.put(
        `${API_URL}/profile/status`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return {
        status: response.data?.status || body.status,
        workLocation: response.data?.workLocation || body.workLocation || null,
        statusMessage: response.data?.statusMessage ?? null,
        statusSource: response.data?.statusSource || body.source || 'MANUAL',
      };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to update status'));
    }
  }
);

export const sendOtp = createAsyncThunk(
  'auth/sendOtp',
  async (mobile, { rejectWithValue }) => {
    try {
      return await otpService.sendOtp(mobile);
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to send OTP'));
    }
  }
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async ({ mobile, otp }, { rejectWithValue }) => {
    try {
      return await otpService.verifyOtp(mobile, otp);
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to verify OTP'));
    }
  }
);

export const resendOtp = createAsyncThunk(
  'auth/resendOtp',
  async (mobile, { rejectWithValue }) => {
    try {
      return await otpService.resendOtp(mobile);
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to resend OTP'));
    }
  }
);

export const sendEmailOtp = createAsyncThunk(
  'auth/sendEmailOtp',
  async ({ username, email }, { rejectWithValue }) => {
    try {
      return await otpService.sendEmailOtp(username, email);
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to send email OTP'));
    }
  }
);

export const verifyEmailOtp = createAsyncThunk(
  'auth/verifyEmailOtp',
  async ({ username, email, otp }, { rejectWithValue }) => {
    try {
      return await otpService.verifyEmailOtp(username, email, otp);
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to verify email OTP'));
    }
  }
);

export const resendEmailOtp = createAsyncThunk(
  'auth/resendEmailOtp',
  async ({ username, email }, { rejectWithValue }) => {
    try {
      return await otpService.resendEmailOtp(username, email);
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to resend email OTP'));
    }
  }
);

const persistedToken = localStorage.getItem('token');

const initialState = {
  user: persistedToken ? readCachedUser() : null,
  token: persistedToken,
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: !!persistedToken,
  loading: false,
  error: null,
  registrationSuccess: false,
  otpLoading: false,
  otpError: null,
  otpSuccessMessage: null,
  emailOtpLoading: false,
  emailOtpError: null,
  emailOtpSuccessMessage: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearOtpState: (state) => {
      state.otpLoading = false;
      state.otpError = null;
      state.otpSuccessMessage = null;
    },
    clearEmailOtpState: (state) => {
      state.emailOtpLoading = false;
      state.emailOtpError = null;
      state.emailOtpSuccessMessage = null;
    },
    clearRegistrationSuccess: (state) => {
      state.registrationSuccess = false;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      persistCachedUser(state.user);
    },
    setUserStatus: (state, action) => {
      if (state.user) {
        const payload = action.payload;
        if (typeof payload === 'string') {
          state.user.status = payload;
        } else if (payload && typeof payload === 'object') {
          if (payload.status != null) state.user.status = payload.status;
          if (payload.workLocation !== undefined) state.user.workLocation = payload.workLocation;
          if (payload.statusMessage !== undefined) state.user.statusMessage = payload.statusMessage;
          if (payload.statusSource !== undefined) state.user.statusSource = payload.statusSource;
          if (payload.onBreak !== undefined) state.user.onBreak = payload.onBreak === true;
        }
        persistCachedUser(state.user);
        syncWorkModeFromStatus(state.user.status, state.user.workLocation);
      }
    },
    updateTokens: (state, action) => {
      const { token, refreshToken } = action.payload;
      state.token = token;
      if (refreshToken) {
        state.refreshToken = refreshToken;
        localStorage.setItem('refreshToken', refreshToken);
      }
      localStorage.setItem('token', token);
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = {
          id: action.payload.id,
          username: action.payload.username,
          email: action.payload.email,
          firstName: action.payload.firstName,
          lastName: action.payload.lastName,
          profilePicture: action.payload.profilePicture,
          role: action.payload.role || 'USER',
          supervisorUserId: action.payload.supervisorUserId || null,
          organizationRoleId: action.payload.organizationRoleId || null,
          organizationRoleName: action.payload.organizationRoleName || null,
          reportsToRoleName: action.payload.reportsToRoleName || null,
          canSeeSpaces: Boolean(action.payload.canSeeSpaces),
          canAssignRoles: Boolean(action.payload.canAssignRoles),
          hasAnnouncementAccess: action.payload.hasAnnouncementAccess || false,
          status: action.payload.status || 'OFFLINE',
          workLocation: action.payload.workLocation || null,
          globalOrganization: action.payload.globalOrganization || false,
          organizationId: action.payload.organizationId || null,
          organizationName: action.payload.organizationName || null,
          statusMessage: action.payload.statusMessage || null,
          statusSource: action.payload.statusSource || null,
          preferredLanguage: action.payload.preferredLanguage || 'en',
        };
        persistCachedUser(state.user);
        syncWorkModeFromStatus(state.user.status, state.user.workLocation);
        // Apply dark mode preference from backend
        if (action.payload.darkMode !== undefined) {
          store.dispatch(setDarkMode(action.payload.darkMode));
        }
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.error = action.payload;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.registrationSuccess = false;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        // If token is null, registration was successful but needs admin approval
        if (action.payload.token) {
          state.isAuthenticated = true;
          state.token = action.payload.token;
        } else {
          state.isAuthenticated = false;
          state.token = null;
          // Set a success message instead of error
          state.error = null;
          state.registrationSuccess = true;
        }
        state.user = {
          id: action.payload.id,
          username: action.payload.username,
          email: action.payload.email,
          firstName: action.payload.firstName,
          lastName: action.payload.lastName,
          profilePicture: action.payload.profilePicture,
          role: action.payload.role || 'USER',
          supervisorUserId: action.payload.supervisorUserId || null,
          organizationRoleId: action.payload.organizationRoleId || null,
          organizationRoleName: action.payload.organizationRoleName || null,
          reportsToRoleName: action.payload.reportsToRoleName || null,
          canSeeSpaces: Boolean(action.payload.canSeeSpaces),
          canAssignRoles: Boolean(action.payload.canAssignRoles),
          hasAnnouncementAccess: action.payload.hasAnnouncementAccess || false,
          globalOrganization: action.payload.globalOrganization || false,
          organizationId: action.payload.organizationId || null,
          organizationName: action.payload.organizationName || null,
          preferredLanguage: action.payload.preferredLanguage || 'en',
        };
        if (state.isAuthenticated) {
          persistCachedUser(state.user);
        }
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.error = action.payload;
      })
      // Organization signup
      .addCase(organizationSignup.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.registrationSuccess = false;
      })
      .addCase(organizationSignup.fulfilled, (state, action) => {
        state.loading = false;
        // If token is null, organization registration was successful but needs admin approval
        if (action.payload.token) {
          state.isAuthenticated = true;
          state.token = action.payload.token;
          state.error = null;
        } else {
          state.isAuthenticated = false;
          state.token = null;
          state.error = null;
          state.registrationSuccess = true;
        }
        state.user = {
          id: action.payload.id,
          username: action.payload.username,
          email: action.payload.email,
          firstName: action.payload.firstName,
          lastName: action.payload.lastName,
          profilePicture: action.payload.profilePicture,
          role: action.payload.role || 'USER',
          supervisorUserId: action.payload.supervisorUserId || null,
          organizationRoleId: action.payload.organizationRoleId || null,
          organizationRoleName: action.payload.organizationRoleName || null,
          reportsToRoleName: action.payload.reportsToRoleName || null,
          canSeeSpaces: Boolean(action.payload.canSeeSpaces),
          canAssignRoles: Boolean(action.payload.canAssignRoles),
          hasAnnouncementAccess: action.payload.hasAnnouncementAccess || false,
          status: action.payload.status || 'OFFLINE',
          workLocation: action.payload.workLocation || null,
          globalOrganization: action.payload.globalOrganization || false,
          organizationId: action.payload.organizationId || null,
          organizationName: action.payload.organizationName || null,
          statusMessage: action.payload.statusMessage || null,
          statusSource: action.payload.statusSource || null,
          preferredLanguage: action.payload.preferredLanguage || 'en',
        };
        if (state.isAuthenticated) {
          persistCachedUser(state.user);
        }
      })
      .addCase(organizationSignup.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.error = action.payload;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.error = null;
      })
      // Get Current User
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        // Keep existing token, don't overwrite with null
        if (action.payload.token) {
          state.token = action.payload.token;
        }
        state.user = {
          id: action.payload.id,
          username: action.payload.username,
          email: action.payload.email,
          firstName: action.payload.firstName,
          lastName: action.payload.lastName,
          profilePicture: action.payload.profilePicture,
          role: action.payload.role || 'USER',
          supervisorUserId: action.payload.supervisorUserId || null,
          organizationRoleId: action.payload.organizationRoleId || null,
          organizationRoleName: action.payload.organizationRoleName || null,
          reportsToRoleName: action.payload.reportsToRoleName || null,
          canSeeSpaces: Boolean(action.payload.canSeeSpaces),
          canAssignRoles: Boolean(action.payload.canAssignRoles),
          hasAnnouncementAccess: action.payload.hasAnnouncementAccess || false,
          status: action.payload.status || 'OFFLINE',
          workLocation: action.payload.workLocation || null,
          globalOrganization: action.payload.globalOrganization || false,
          organizationId: action.payload.organizationId || null,
          organizationName: action.payload.organizationName || null,
          statusMessage: action.payload.statusMessage || null,
          statusSource: action.payload.statusSource || null,
          preferredLanguage: action.payload.preferredLanguage || 'en',
        };
        persistCachedUser(state.user);
        syncWorkModeFromStatus(state.user.status, state.user.workLocation);
        // Apply dark mode preference from backend
        if (action.payload.darkMode !== undefined) {
          store.dispatch(setDarkMode(action.payload.darkMode));
        }
        state.error = null;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        if (state.user) {
          const payload = action.payload;
          if (typeof payload === 'string') {
            state.user.status = payload;
          } else {
            if (payload.status) state.user.status = payload.status;
            if (payload.workLocation !== undefined) state.user.workLocation = payload.workLocation;
            if (payload.statusMessage !== undefined) state.user.statusMessage = payload.statusMessage ?? null;
            if (payload.statusSource) state.user.statusSource = payload.statusSource;
          }
          persistCachedUser(state.user);
          syncWorkModeFromStatus(state.user.status, state.user.workLocation);
        }
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        const status = payload && typeof payload === 'object' ? payload.status : null;
        const persistedToken = state.token || localStorage.getItem('token');
        const isAuthFailure = status === 401
          || payload === 'No token found'
          || payload?.message === 'No token found';
        const isTransient = !isAuthFailure && (
          (payload && typeof payload === 'object' && payload.transient)
          || Boolean(persistedToken)
        );

        // Network / tab-lock / backend-down must not force a new login.
        if (isTransient && persistedToken) {
          state.token = persistedToken;
          state.isAuthenticated = true;
          if (!state.user) {
            state.user = readCachedUser();
          }
          state.error = null;
          return;
        }

        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.error = payload && typeof payload === 'object' ? payload.message : payload;
        persistCachedUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      })
      // Send OTP
      .addCase(sendOtp.pending, (state) => {
        state.otpLoading = true;
        state.otpError = null;
        state.otpSuccessMessage = null;
      })
      .addCase(sendOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.otpError = null;
        state.otpSuccessMessage = action.payload?.message || 'OTP sent successfully';
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.otpError = action.payload;
      })
      // Verify OTP
      .addCase(verifyOtp.pending, (state) => {
        state.otpLoading = true;
        state.otpError = null;
        state.otpSuccessMessage = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.otpError = null;
        state.otpSuccessMessage = action.payload?.message || 'OTP verified successfully';
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.otpError = action.payload;
      })
      // Resend OTP
      .addCase(resendOtp.pending, (state) => {
        state.otpLoading = true;
        state.otpError = null;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.otpError = null;
        state.otpSuccessMessage = action.payload?.message || 'OTP resent successfully';
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.otpError = action.payload;
      })
      // Send Email OTP
      .addCase(sendEmailOtp.pending, (state) => {
        state.emailOtpLoading = true;
        state.emailOtpError = null;
        state.emailOtpSuccessMessage = null;
      })
      .addCase(sendEmailOtp.fulfilled, (state, action) => {
        state.emailOtpLoading = false;
        state.emailOtpError = null;
        state.emailOtpSuccessMessage = action.payload?.message || 'Verification email sent successfully';
      })
      .addCase(sendEmailOtp.rejected, (state, action) => {
        state.emailOtpLoading = false;
        state.emailOtpError = action.payload;
      })
      // Verify Email OTP
      .addCase(verifyEmailOtp.pending, (state) => {
        state.emailOtpLoading = true;
        state.emailOtpError = null;
        state.emailOtpSuccessMessage = null;
      })
      .addCase(verifyEmailOtp.fulfilled, (state, action) => {
        state.emailOtpLoading = false;
        state.emailOtpError = null;
        state.emailOtpSuccessMessage = action.payload?.message || 'Email OTP verified successfully';
      })
      .addCase(verifyEmailOtp.rejected, (state, action) => {
        state.emailOtpLoading = false;
        state.emailOtpError = action.payload;
      })
      // Resend Email OTP
      .addCase(resendEmailOtp.pending, (state) => {
        state.emailOtpLoading = true;
        state.emailOtpError = null;
      })
      .addCase(resendEmailOtp.fulfilled, (state, action) => {
        state.emailOtpLoading = false;
        state.emailOtpError = null;
        state.emailOtpSuccessMessage = action.payload?.message || 'Verification email resent successfully';
      })
      .addCase(resendEmailOtp.rejected, (state, action) => {
        state.emailOtpLoading = false;
        state.emailOtpError = action.payload;
      })
      .addCase(updatePreferredLanguage.fulfilled, (state, action) => {
        if (!state.user) return;
        state.user.preferredLanguage = action.payload || 'en';
        persistCachedUser(state.user);
      });
  },
});

export const { clearError, clearOtpState, clearEmailOtpState, clearRegistrationSuccess, setUser, setUserStatus, updateTokens } = authSlice.actions;
export default authSlice.reducer;

